import { useEffect, useState } from 'react';

const MAX_TIMEOUT_MS = 2_147_483_647;

export const isBeforeDeadline = (deadline: string | null, now = Date.now()): boolean => {
  if (deadline === null) return true;
  const deadlineMs = Date.parse(deadline);
  return Number.isFinite(deadlineMs) && now < deadlineMs;
};

export const useBeforeDeadline = (deadline: string | null): boolean => {
  const [, refresh] = useState(0);
  const beforeDeadline = isBeforeDeadline(deadline);

  useEffect(() => {
    if (deadline === null) return undefined;
    const deadlineMs = Date.parse(deadline);
    if (!Number.isFinite(deadlineMs)) return undefined;

    let timeout: number;
    const scheduleRefresh = () => {
      timeout = window.setTimeout(
        () => {
          refresh((value) => value + 1);
          if (Date.now() < deadlineMs) scheduleRefresh();
        },
        Math.min(MAX_TIMEOUT_MS, Math.max(0, deadlineMs - Date.now())),
      );
    };
    scheduleRefresh();
    const onResume = () => refresh((value) => value + 1);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onResume();
    };

    window.addEventListener('focus', onResume);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('focus', onResume);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [deadline]);

  return beforeDeadline;
};
